<script setup lang="ts">
  import { computed, defineProps, onMounted, ref, Ref, inject } from 'vue'
  import { QuillEditor } from '@vueup/vue-quill'
  import '@vueup/vue-quill/dist/vue-quill.snow.css';
  import draggable from "vuedraggable";

  import { useEventStore } from '@/store/event'
  import EventPricing from '@/components/Event/partials/EventPricing.vue'
  import EventGroupTickets from '@/components/Event/partials/EventGroupTickets.vue'
  import EventGroupPricing from '@/components/Event/partials/EventGroupPricing.vue'
  import EventInquiryForm from '@/components/Event/partials/EventInquiryForm.vue'
  import EventPricingInformation from '@/components/Event/partials/EventPricingInformation.vue'
  import EventAdditionalOffers from '@/components/Event/partials/EventAdditionalOffers.vue'
  import EventAddress from '@/components/Event/partials/EventAddress.vue'
  import EventContact from '@/components/Event/partials/EventContact.vue'
  import EventOrganizer from '@/components/Event/partials/EventOrganizer.vue'
  import EventNotifications from '@/components/Event/partials/EventNotifications.vue'
  import EventDownloads from '@/components/Event/partials/EventDownloads.vue'

  const store = useEventStore()

  const serverErrors = inject<Ref<any>>('serverErrors')

  const fixedContentTypes = [
    'subtitle',
    'excerpt',
    'description',
    'duration',
    'ticket_shop_url'
  ];

  const optionalContent = computed(() => {
    return store.content
      .filter(item => fixedContentTypes.indexOf(item.type) === -1 && item.visible === true)
      .sort((a, b) => a.order - b.order);
  });

  const onDragged = (event) => {
    let oldIndex = event.moved.oldIndex + 1
    let newIndex = event.moved.newIndex + 1

    store.content.forEach((item) => {
      if (item.order === oldIndex) {
        item.order = newIndex
      } else if (oldIndex < newIndex) {
        if (item.order > oldIndex && item.order <= newIndex) {
          item.order = item.order - 1
        }
      } else if (oldIndex > newIndex) {
        if (item.order < oldIndex && item.order >= newIndex) {
          item.order = item.order + 1
        }
      }
    })
  }

  const addContent = (type: string) => {
    const contentItem = store.getContent(type);

    // The new item will be placed immediately after the last active item.
    // optionalContent contains only visible items, so its length is the count of active items.
    const newOrder = optionalContent.value.length + 1;

    store.content.forEach((item) => {
      // Shift all items (mostly inactive ones) that are currently at this position or below
      // down by 1 to make space.
      if (fixedContentTypes.indexOf(item.type) === -1 && item.type !== type) {
        if (item.order >= newOrder) {
          item.order = item.order + 1;
        }
      }
    });

    contentItem.order = newOrder;
    contentItem.visible = true;
  }

  const onRemove = (item) => {
    const oldOrder = item.order;

    // Shift all items that were below the removed item up by 1 to close the gap.
    store.content.forEach((c) => {
      if (fixedContentTypes.indexOf(c.type) === -1) {
        if (c.order > oldOrder) {
          c.order = c.order - 1;
        }
      }
    });

    // Move the removed (now inactive) item to the very end of the list.
    const allOptional = store.content.filter(c => fixedContentTypes.indexOf(c.type) === -1);
    item.order = allOptional.length;

    // Ensure it is marked invisible
    item.visible = false;
  }

  const getValue = (type) => store.content.filter(item => {
      return item.type === type;
    }).shift();

  const isVisible = (type) => getValue(type)?.visible;

  const getError = (field: string) => {
    if (serverErrors?.value && serverErrors.value[field]) {
      return serverErrors.value[field][0]
    }
    return null
  }

  const getIndex = (type) => store.content.findIndex(item => item.type === type);

  const getContentError = (type: string, field: string) => {
    const index = getIndex(type);
    if (index === -1) return null;

    // Check if field already contains the prefix or acts as relative path
    // The errors are like content.1.data.text
    // So prefix is content.{index}.data.

    const key = 'content.' + index + '.data.' + field;
     if (serverErrors?.value && serverErrors.value[key]) {
      return serverErrors.value[key][0]
    }
    return null
  }

  const allContentIsVisible = () => {
    return store.content.filter(item => {
      return item.visible === false;
    }).length === 0;
  }
</script>

<template>
  <div>
    <div class="card w-full bg-base-100  ">
      <div class="card-body">
        <h2 class="card-title">Allgemein</h2>
        <div>
          <label class="form-control w-full">
            <div class="label">
              <span class="label-text">Titel*</span>
          </div>

          <input id="subtitle" type="text" v-model="store.title" class="input input-bordered input-sm w-full" :class="{ 'input-error': getError('title') }" name="title" required>
            <div class="label" v-if="getError('title')">
              <span class="label-text-alt text-error">
                  <strong>{{ getError('title') }}</strong>
              </span>
            </div>
          <!-- @if ($errors->has('password'))
            <div class="label">
                <span class="label-text-alt text-error">
                    <strong>{{ $errors -> first('password') }}</strong>
                </span>
            </div>
          @endif -->
          </label>
        </div>
        <div>
          <label class="form-control w-full">
            <div class="label">
              <span class="label-text">Untertitel*</span>
            </div>

            <input id="subtitle" type="text" v-model="getValue('subtitle').data.text" class="input input-bordered input-sm w-full" :class="{ 'input-error': getContentError('subtitle', 'text') }" name="subtitle" required>
             <div class="label" v-if="getContentError('subtitle', 'text')">
              <span class="label-text-alt text-error">
                  <strong>{{ getContentError('subtitle', 'text') }}</strong>
              </span>
            </div>
          </label>
        </div>
        <div>
          <label class="form-control">
            <div class="label">
              <span class="label-text">Kurzbeschreibung*</span>
            </div>
            <textarea v-model="getValue('excerpt').data.text" class="textarea textarea-bordered textarea-sm" :class="{ 'textarea-error': getContentError('excerpt', 'text') }" rows="4" required></textarea>
            <div class="label" v-if="getContentError('excerpt', 'text')">
              <span class="label-text-alt text-error">
                  <strong>{{ getContentError('excerpt', 'text') }}</strong>
              </span>
            </div>
          </label>
        </div>
        <div>
          <label class="form-control">
            <div class="label">
              <span class="label-text">Beschreibung*</span>
            </div>

            <QuillEditor v-model:content="getValue('description').data.text" contentType="html" theme="snow" class="min-h-48 [&>.ql-editor]:min-h-48 rounded-b-lg [&_p]:!mb-2"/>

             <div class="label" v-if="getContentError('description', 'text')">
              <span class="label-text-alt text-error">
                  <strong>{{ getContentError('description', 'text') }}</strong>
              </span>
            </div>
          </label>
        </div>
        <div>
          <div class="flex items-center mt-3">
            <input type="checkbox" class="toggle toggle-sm" v-model="store.getContent('description').data.has_linklist" />
            <label class="label-text ml-2">Externe Links hinzufügen</label>
          </div>
          <div v-if="store.getContent('description').data.has_linklist" class="mt-4">
            <div v-for="(link, index) in store.getContent('description').data.linklist" :key="index" class="flex items-start justify-between sm:mb-6 mb-4">
              <div class="flex flex-wrap mr-2 w-[95%] justify-between">
                <div class="w-full mb-2">
                  <input type="text" v-model="link.label" class="input input-bordered input-sm w-full" :class="{ 'input-error': getContentError('description', 'linklist.' + index + '.label') }" placeholder="Bezeichnung">
                   <div class="label" v-if="getContentError('description', 'linklist.' + index + '.label')">
                    <span class="label-text-alt text-error">
                        <strong>{{ getContentError('description', 'linklist.' + index + '.label') }}</strong>
                    </span>
                  </div>
                </div>

                <div class="w-full sm:w-[49%] mb-2 sm:mb-0">
                  <input type="text" v-model="link.text" class="input input-bordered input-sm w-full" :class="{ 'input-error': getContentError('description', 'linklist.' + index + '.text') }" placeholder="Weitere Infos">
                   <div class="label" v-if="getContentError('description', 'linklist.' + index + '.text')">
                    <span class="label-text-alt text-error">
                        <strong>{{ getContentError('description', 'linklist.' + index + '.text') }}</strong>
                    </span>
                  </div>
                </div>

                <div class="w-full sm:w-[49%]">
                  <input type="text" v-model="link.url" class="input input-bordered input-sm w-full" :class="{ 'input-error': getContentError('description', 'linklist.' + index + '.url') }" placeholder="URL">
                   <div class="label" v-if="getContentError('description', 'linklist.' + index + '.url')">
                    <span class="label-text-alt text-error">
                        <strong>{{ getContentError('description', 'linklist.' + index + '.url') }}</strong>
                    </span>
                  </div>
                </div>
              </div>
              <button class="btn btn-sm btn-square text-error-content" @click.prevent="() => store.getContent('description').data.linklist.splice(index, 1)">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 fill-error" viewBox="0 0 24 24"><title>delete-outline</title><path d="M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19M8,9H16V19H8V9M15.5,4L14.5,3H9.5L8.5,4H5V6H19V4H15.5Z" /></svg>
              </button>
            </div>
            <button class="btn btn-block btn-success btn-sm text-white" @click.prevent="() => store.getContent('description').data.linklist.push({ label: '', text: '', url: '' })">+ Link hinzufügen</button>
          </div>
          <div class="label" v-if="getContentError('description', 'linklist')">
            <span class="label-text-alt text-error">
                <strong>{{ getContentError('description', 'linklist') }}</strong>
            </span>
          </div>
        </div>
        <div class="flex">
          <div class="w-1/2 mr-2">
            <label class="form-control w-full">
              <div class="label">
                <span class="label-text">
                  Dauer (Freitext)
                  <div class="tooltip align-bottom" data-tip="Die Dauer wird in der Regel aus dem ersten Termin des jeweiligen Tages errechnet und angezeigt. Sie können mit diesem Feld den Freitext für alle Tage überschreiben, in dem sie das Feld befüllen.">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 fill-info cursor-pointer" viewBox="0 0 24 24"><title>information-outline</title><path d="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z" /></svg>
                  </div>
                </span>
              </div>

              <input id="duration" type="text" v-model="getValue('duration').data.text" class="input input-bordered input-sm w-full" :class="{ 'input-error': getContentError('duration', 'text') }" name="duration">
              <div class="label" v-if="getContentError('duration', 'text')">
                <span class="label-text-alt text-error">
                    <strong>{{ getContentError('duration', 'text') }}</strong>
                </span>
              </div>
            </label>
          </div>
          <div class="w-1/2 ml-2">
            <label class="form-control w-full">
              <div class="label">
                <span class="label-text">URL zum Ticketshop</span>
              </div>

              <input id="ticket_shop_url" type="text" v-model="getValue('ticket_shop_url').data.url" class="input input-bordered input-sm w-full" :class="{ 'input-error': getContentError('ticket_shop_url', 'url') }" name="ticket_shop_url">
              <div class="label" v-if="getContentError('ticket_shop_url', 'url')">
                <span class="label-text-alt text-error">
                    <strong>{{ getContentError('ticket_shop_url', 'url') }}</strong>
                </span>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
    <draggable
      :list="optionalContent"
      handle=".handle"
      item-key="title"
      @change="onDragged"
    >
      <template #item="{element}">
        <div>
          <EventPricing v-if="element.type === 'pricing'" @remove="onRemove" :index="store.content.indexOf(element)"/>
          <EventGroupTickets v-if="element.type === 'group_tickets'" @remove="onRemove" :index="store.content.indexOf(element)"/>
          <EventGroupPricing v-if="element.type === 'group_pricing'" @remove="onRemove" :index="store.content.indexOf(element)"/>
          <EventInquiryForm v-if="element.type === 'inquiry_form'" @remove="onRemove" :index="store.content.indexOf(element)"/>
          <EventPricingInformation v-if="element.type === 'pricing_information'" @remove="onRemove" :index="store.content.indexOf(element)"/>
          <EventAdditionalOffers v-if="element.type === 'additional_offers'" @remove="onRemove" :index="store.content.indexOf(element)"/>
          <EventAddress v-if="element.type === 'address'" @remove="onRemove" :index="store.content.indexOf(element)"/>
          <EventContact v-if="element.type === 'contact'" @remove="onRemove" :index="store.content.indexOf(element)"/>
          <EventOrganizer v-if="element.type === 'organizer'" @remove="onRemove" :index="store.content.indexOf(element)"/>
          <EventNotifications v-if="element.type === 'notifications'" @remove="onRemove" :index="store.content.indexOf(element)"/>
          <EventDownloads v-if="element.type === 'downloads'" @remove="onRemove" :index="store.content.indexOf(element)"/>
        </div>
      </template>
    </draggable>
    <div class="dropdown dropdown-top mt-4 w-full">
      <div tabindex="0" role="button" class="btn btn-wide bg-neutral text-neutral-content m-1 w-full">+ Inhaltselement hinzufügen</div>
      <ul tabindex="0" class="dropdown-content z-[1] menu p-2 shadow bg-base-200 rounded-box w-full">
        <li v-if="!isVisible('pricing')"><button @click.prevent="addContent('pricing')">Preise</button></li>
        <li v-if="!isVisible('group_tickets')"><button @click.prevent="addContent('group_tickets')">Gruppenführungen</button></li>
        <li v-if="!isVisible('group_pricing')"><button @click.prevent="addContent('group_pricing')">Preise – Gruppen</button></li>
        <li v-if="!isVisible('inquiry_form')"><button @click.prevent="addContent('inquiry_form')">Anfrageformular</button></li>
        <li v-if="!isVisible('pricing_information')"><button @click.prevent="addContent('pricing_information')">Preise – Zusatzinformationen</button></li>
        <li v-if="!isVisible('additional_offers')"><button @click.prevent="addContent('additional_offers')">Weitere Angebote</button></li>
        <li v-if="!isVisible('address')"><button @click.prevent="addContent('address')">Adresse</button></li>
        <li v-if="!isVisible('contact')"><button @click.prevent="addContent('contact')">Kontakt</button></li>
        <li v-if="!isVisible('organizer')"><button @click.prevent="addContent('organizer')">Veranstalter</button></li>
        <li v-if="!isVisible('notifications')"><button @click.prevent="addContent('notifications')">Hinweise</button></li>
        <li v-if="!isVisible('downloads')"><button @click.prevent="addContent('downloads')">Downloads</button></li>
      </ul>
    </div>
  </div>
</template>
